import { useRef, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeft,
  CheckCircle2,
  Download,
  Loader2,
  MicVocal,
  ScanFace,
  Upload,
  UserSquare2,
  Video as VideoIcon,
  XCircle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { uploadFile } from '@/shared/api/app-client'
import type { ApiResponse, AssetUploadResult } from '@/shared/api/app-types'
import {
  DEMO_VIDEOS,
  RESOLUTION_OPTIONS,
  aliyunDigitalHumanService,
  type DigitalHumanResolution,
} from '@/features/digital-human/services/aliyun-digital-human-service'

/** 演示视频展示区块（共享组件） */
export function DemoVideosSection({
  title = '效果展示',
  className,
}: {
  title?: string
  className?: string
}) {
  return (
    <div className={`space-y-4 ${className ?? ''}`}>
      <div className="text-sm font-medium text-foreground">{title}</div>
      <div className="grid gap-4 sm:grid-cols-3">
        {DEMO_VIDEOS.map((video) => (
          <div key={video.src} className="overflow-hidden rounded-lg border border-border bg-card">
            <video
              src={video.src}
              controls
              preload="metadata"
              className="aspect-video w-full bg-black"
            />
            <div className="px-3 py-2">
              <div className="text-xs font-medium text-foreground">{video.title}</div>
              <div className="mt-0.5 text-[11px] text-muted-foreground">{video.description}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function DigitalHumanCanvas() {
  const navigate = useNavigate()
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [audioFile, setAudioFile] = useState<File | null>(null)
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const [resolution, setResolution] = useState<DigitalHumanResolution>('480P')
  const [isDetecting, setIsDetecting] = useState(false)
  const [detectResult, setDetectResult] = useState<{ checkPass: boolean; humanoid: boolean } | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [progress, setProgress] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [savedAsset, setSavedAsset] = useState<AssetUploadResult | null>(null)
  const [result, setResult] = useState<{ file: File; objectUrl: string } | null>(null)

  const imageInputRef = useRef<HTMLInputElement>(null)
  const audioInputRef = useRef<HTMLInputElement>(null)

  const canGenerate = !!imageFile && !!audioFile && !isGenerating

  function handleImageChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setError('请上传图片文件。')
      return
    }
    if (imageUrl) URL.revokeObjectURL(imageUrl)
    setImageFile(file)
    setImageUrl(URL.createObjectURL(file))
    setDetectResult(null)
    setError(null)
    event.target.value = ''
  }

  function handleAudioChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('audio/')) {
      setError('请上传音频文件（wav / mp3）。')
      return
    }
    if (audioUrl) URL.revokeObjectURL(audioUrl)
    setAudioFile(file)
    setAudioUrl(URL.createObjectURL(file))
    setError(null)
    event.target.value = ''
  }

  async function handleDetect() {
    if (!imageFile) return
    setIsDetecting(true)
    setError(null)
    setDetectResult(null)
    try {
      const result = await aliyunDigitalHumanService.detectImage({ imageFile })
      setDetectResult(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : '图像检测失败。')
    } finally {
      setIsDetecting(false)
    }
  }

  async function handleGenerate() {
    if (!canGenerate || !imageFile || !audioFile) return
    setIsGenerating(true)
    setError(null)
    setProgress(null)
    setSavedAsset(null)

    try {
      if (result) URL.revokeObjectURL(result.objectUrl)
      const generated = await aliyunDigitalHumanService.generateVideo({
        imageFile,
        audioFile,
        resolution,
        onProgress: setProgress,
      })
      setResult({ file: generated.file, objectUrl: generated.objectUrl })
      setProgress('数字人视频生成完成，可以下载或保存到素材库。')
    } catch (err) {
      setError(err instanceof Error ? err.message : '数字人视频生成失败。')
      setProgress(null)
    } finally {
      setIsGenerating(false)
    }
  }

  function handleDownload() {
    if (!result) return
    const anchor = document.createElement('a')
    anchor.href = result.objectUrl
    anchor.download = result.file.name
    anchor.click()
  }

  async function handleSaveAsset() {
    if (!result || isSaving) return
    setIsSaving(true)
    setError(null)
    try {
      const uploadRes = await uploadFile<ApiResponse<AssetUploadResult>>(
        '/api/assets/upload',
        result.file,
      )
      setSavedAsset(uploadRes.data)
      setProgress('已保存到素材库。')
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存到素材库失败。')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="mx-auto max-w-7xl px-6 py-8">
      {/* Header */}
      <div className="mb-8 flex items-center gap-4">
        <Link
          to="/app"
          className="inline-flex size-9 items-center justify-center rounded-lg border border-border bg-card text-foreground transition-colors hover:bg-accent"
        >
          <ArrowLeft className="size-4" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">数字人</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            上传人像图片和音频，生成口型同步、表情自然的说话视频。
          </p>
        </div>
      </div>

      <div className="grid gap-8 xl:grid-cols-[1.15fr_0.85fr]">
        {/* Left: inputs */}
        <section className="space-y-6">
          {/* Image upload */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">人像图片</Label>
            <input
              ref={imageInputRef}
              type="file"
              className="hidden"
              accept="image/jpeg,image/png,image/webp,image/bmp"
              onChange={handleImageChange}
            />
            <button
              type="button"
              onClick={() => imageInputRef.current?.click()}
              className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-card px-6 py-8 text-center transition-colors hover:border-primary/40 hover:bg-accent"
            >
              {imageUrl ? (
                <>
                  <img
                    src={imageUrl}
                    alt="人像预览"
                    className="max-h-48 rounded-lg border border-border object-contain"
                  />
                  <span className="text-xs text-muted-foreground">
                    {imageFile?.name} · {imageFile ? formatFileSize(imageFile.size) : ''}
                  </span>
                  <span className="text-xs text-muted-foreground">点击重新选择</span>
                </>
              ) : (
                <>
                  <UserSquare2 className="size-7 text-muted-foreground/50" />
                  <span className="text-sm font-medium text-foreground">点击上传人像图片</span>
                  <span className="text-xs text-muted-foreground">
                    肖像/半身/全身均可，支持 jpg/png/webp，建议 400px 以上
                  </span>
                </>
              )}
            </button>

            {/* Detect button + result */}
            {imageFile ? (
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void handleDetect()}
                  disabled={isDetecting}
                >
                  {isDetecting ? <Loader2 className="size-3.5 animate-spin" /> : <ScanFace className="size-3.5" />}
                  检测图片
                </Button>
                {detectResult ? (
                  detectResult.checkPass ? (
                    <span className="flex items-center gap-1.5 text-xs text-emerald-600">
                      <CheckCircle2 className="size-3.5" />
                      检测通过，适合生成数字人
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 text-xs text-destructive">
                      <XCircle className="size-3.5" />
                      {detectResult.humanoid ? '图片不合规，请更换' : '未检测到人像，请更换'}
                    </span>
                  )
                ) : null}
              </div>
            ) : null}
          </div>

          {/* Audio upload */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">驱动音频</Label>
            <input
              ref={audioInputRef}
              type="file"
              className="hidden"
              accept="audio/wav,audio/mpeg,.wav,.mp3"
              onChange={handleAudioChange}
            />
            <button
              type="button"
              onClick={() => audioInputRef.current?.click()}
              className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-card px-6 py-8 text-center transition-colors hover:border-primary/40 hover:bg-accent"
            >
              {audioUrl ? (
                <>
                  <MicVocal className="size-7 text-primary" />
                  <span className="text-sm font-medium text-foreground">
                    {audioFile?.name ?? '已选择音频'}
                  </span>
                  {audioFile ? (
                    <span className="text-xs text-muted-foreground">
                      {formatFileSize(audioFile.size)}
                    </span>
                  ) : null}
                  <audio controls src={audioUrl} className="mt-2 w-full max-w-sm" />
                  <span className="text-xs text-muted-foreground">点击重新选择</span>
                </>
              ) : (
                <>
                  <MicVocal className="size-7 text-muted-foreground/50" />
                  <span className="text-sm font-medium text-foreground">点击上传音频</span>
                  <span className="text-xs text-muted-foreground">
                    清晰人声，wav / mp3，时长 &lt; 20秒
                  </span>
                </>
              )}
            </button>
          </div>

          {/* Resolution */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">分辨率</Label>
            <Select
              value={resolution}
              onValueChange={(value) => setResolution(value as DigitalHumanResolution)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RESOLUTION_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label} · {option.pricePerSec}元/秒
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              视频比例跟随图片，按秒计费。
            </p>
          </div>

          {/* Generate */}
          <Button onClick={() => void handleGenerate()} disabled={!canGenerate} className="w-full sm:w-auto">
            {isGenerating ? <Loader2 className="size-4 animate-spin" /> : <VideoIcon className="size-4" />}
            生成数字人视频
          </Button>

          {progress ? (
            <div className="rounded-lg border border-primary/15 bg-primary/5 px-3.5 py-2.5 text-sm text-primary">
              {progress}
            </div>
          ) : null}

          {error ? (
            <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-3.5 py-2.5 text-sm text-destructive">
              {error}
            </div>
          ) : null}
        </section>

        {/* Right: preview */}
        <section className="space-y-5">
          {result ? (
            <>
              <div className="space-y-3 rounded-xl border border-border bg-card p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground">数字人视频</div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {resolution} · {formatFileSize(result.file.size)}
                    </div>
                  </div>
                  <div className="rounded-lg bg-primary/10 p-2.5 text-primary">
                    <VideoIcon className="size-5" />
                  </div>
                </div>
                <video controls src={result.objectUrl} className="w-full rounded-lg border border-border" />
              </div>

              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={handleDownload} disabled={!result}>
                  <Download className="size-4" />
                  下载视频
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => void handleSaveAsset()}
                  disabled={!result || isSaving}
                >
                  {isSaving ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
                  保存到素材库
                </Button>
              </div>

              <div className="space-y-3">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate({ to: '/app/assets', search: { tab: 'video' } })}
                >
                  去素材库查看
                </Button>
              </div>

              {savedAsset ? (
                <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3.5 py-2.5 text-sm text-emerald-700">
                  <div className="flex items-center gap-2 font-medium">
                    <CheckCircle2 className="size-4" />
                    已保存到素材库
                  </div>
                  <div className="mt-1 text-xs text-emerald-700/80">
                    素材 ID: {savedAsset.id} · 文件名: {savedAsset.fileName}
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <div className="flex min-h-[280px] flex-col items-center justify-center rounded-xl border border-dashed border-border px-8 text-center">
              {isGenerating ? (
                <Loader2 className="size-8 animate-spin text-muted-foreground/50" />
              ) : (
                <VideoIcon className="size-8 text-muted-foreground/50" />
              )}
              <h3 className="mt-4 text-sm font-medium text-foreground">
                {isGenerating ? '数字人视频生成中，请耐心等待…' : '生成结果会在这里展示'}
              </h3>
              <p className="mt-1.5 max-w-xs text-xs leading-5 text-muted-foreground">
                {isGenerating
                  ? '数字人生成通常需要 5-10 分钟，请勿离开页面。'
                  : '在左侧上传图片和音频后点击生成。'}
              </p>
            </div>
          )}
        </section>
      </div>

      {/* 演示视频展示 */}
      <div className="mt-12">
        <DemoVideosSection title="数字人效果展示" />
      </div>
    </div>
  )
}
